import type { Dispatch, SetStateAction } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { FilterOption } from '@odo/shared-ui';
import AssigneeSelector from '../../../conversations/AssigneeSelector';
import { useUser } from '../../../../providers/UserProvider';
import type {
    Communication,
    CommunicationPatientRef,
    CommunicationStatus
} from '../../../../services/communications';
import type { PracticeDetail } from '../../../../services/practiceDetail';
import { CategorySelector } from './CategorySelector';
import type { CommunicationFormValues } from './communicationFormValues';
import { LinkedPatientsCard } from './LinkedPatientsCard';
import { MetaCard } from './MetaCard';
import { PracticeMetaCard } from './PracticeMetaCard';
import { RecentCommunicationsSection } from './RecentCommunicationsSection';

const STATUS_DOT: Record<CommunicationStatus, string> = {
    open: '#2C6F67',
    resolved: '#059669'
};

interface CommunicationFormMetaRailProps {
    practiceId: string | null;
    /** Null until a practice is loaded - always non-null when `practiceIsEditable` is false. */
    practice: PracticeDetail | null;
    /** True only on the practice-less entry point - see PracticeMetaCard. */
    practiceIsEditable: boolean;
    practiceOptions: readonly FilterOption[];
    isPracticeOptionsLoading: boolean;
    onPracticeChange: (practiceId: string) => void;
    disabled: boolean;
    totalCommunications: number;
    openCommunicationsCount: number;
    form: CommunicationFormValues;
    setForm: Dispatch<SetStateAction<CommunicationFormValues>>;
    lastCommunications: Communication[];
    isLastCommunicationsLoading: boolean;
    onNavigate: (path: string) => void;
}

// Thread fields for the entry in CommunicationFormMain, in labelled cards
// matching CommunicationMetaRail.tsx's structure - no Delete (nothing to
// delete yet), plus a "Recent from this practice" card once one's picked.
export function CommunicationFormMetaRail({
    practiceId,
    practice,
    practiceIsEditable,
    practiceOptions,
    isPracticeOptionsLoading,
    onPracticeChange,
    disabled,
    totalCommunications,
    openCommunicationsCount,
    form,
    setForm,
    lastCommunications,
    isLastCommunicationsLoading,
    onNavigate
}: CommunicationFormMetaRailProps) {
    const { user } = useUser();

    const handlePatientsChange = (patients: CommunicationPatientRef[]) => {
        setForm((f) => ({ ...f, patients }));
    };

    const handleAssign = (email: string) => {
        setForm((f) => ({ ...f, assigneeEmail: email }));
    };

    const handleUnassign = () => {
        setForm((f) => ({ ...f, assigneeEmail: null }));
    };

    return (
        <aside className="w-[316px] shrink-0 h-full overflow-y-auto pr-1 flex flex-col gap-3.5">
            <PracticeMetaCard
                editable={practiceIsEditable}
                practiceId={practiceId}
                practice={practice}
                totalCommunications={totalCommunications}
                openCommunicationsCount={openCommunicationsCount}
                practiceOptions={practiceOptions}
                isPracticeOptionsLoading={isPracticeOptionsLoading}
                onPracticeChange={onPracticeChange}
            />

            <MetaCard title="Status">
                <div className="relative">
                    <span
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full pointer-events-none"
                        style={{ background: STATUS_DOT[form.status] }}
                    />
                    <select
                        value={form.status}
                        disabled={disabled}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                status: e.target.value as CommunicationStatus
                            }))
                        }
                        className="w-full h-9 pl-6 pr-8 border border-gray-300 rounded-lg text-[13px] text-gray-800 bg-white appearance-none focus:outline-none focus:border-gray-800 focus:ring-2 focus:ring-gray-200 disabled:opacity-50"
                    >
                        <option value="open">Open</option>
                        <option value="resolved">Resolved</option>
                    </select>
                    <ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
            </MetaCard>

            <MetaCard title="Categories">
                <CategorySelector
                    categories={form.categories}
                    disabled={disabled}
                    onChange={(categories) => setForm((f) => ({ ...f, categories }))}
                />
            </MetaCard>

            <LinkedPatientsCard
                practiceId={practiceId}
                patients={form.patients}
                onChange={handlePatientsChange}
                title="Patients"
            />

            {/* Assignment uses the same selector (and the same all-ODO
                member source) as conversations and the work queue, so
                "assign someone" behaves identically wherever staff meet it. */}
            <MetaCard title="Assignee">
                <AssigneeSelector
                    assignedTo={form.assigneeEmail}
                    // No roles: threads are not team-scoped, so the picker offers everyone at ODO.
                    onAssign={handleAssign}
                    onUnassign={handleUnassign}
                    currentUserEmail={user?.email ?? ''}
                    dropdownAlign="right"
                />
            </MetaCard>

            {practiceId && (
                <RecentCommunicationsSection
                    practiceId={practiceId}
                    communications={lastCommunications}
                    isLoading={isLastCommunicationsLoading}
                    onNavigate={onNavigate}
                />
            )}
        </aside>
    );
}
